import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Carrusel horizontal sobre scroll nativo.
 *
 * No mueve nada por su cuenta: el desplazamiento lo hace el navegador con
 * scroll-snap, asi que funciona con el dedo, con el trackpad y con el teclado
 * sin codigo propio. Este hook solo aporta los botones de atajo para mouse y
 * el estado de los extremos.
 *
 * La pista necesita, en CSS: `overflow-x: auto`, `scroll-snap-type: x mandatory`
 * y -si tiene padding horizontal- `scroll-padding-inline` con ese mismo valor.
 * Sin lo ultimo el reposo no queda en scrollLeft 0 y el boton de "anterior"
 * nunca se deshabilita.
 */
export function useCarousel<T extends HTMLElement = HTMLDivElement>(
  /** Selector de una tarjeta, para medir cuanto avanza cada paso. */
  itemSelector: string,
  /** Separacion entre tarjetas en px (el `gap` del CSS). */
  gap = 24,
) {
  const ref = useRef<T>(null);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);

  const syncEdges = useCallback(() => {
    const el = ref.current;
    if (!el) return;

    const max = el.scrollWidth - el.clientWidth;
    setAtStart(el.scrollLeft <= 1);
    // Margen de 1px: los navegadores redondean scrollLeft y el ultimo pixel
    // nunca se alcanza exacto.
    setAtEnd(el.scrollLeft >= max - 1);
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    syncEdges();
    el.addEventListener("scroll", syncEdges, { passive: true });
    window.addEventListener("resize", syncEdges);

    return () => {
      el.removeEventListener("scroll", syncEdges);
      window.removeEventListener("resize", syncEdges);
    };
  }, [syncEdges]);

  const scrollByCard = useCallback(
    (direction: 1 | -1) => {
      const el = ref.current;
      if (!el) return;

      // Se mide una tarjeta real en vez de fijar un numero: asi el paso
      // acompaña los breakpoints sin duplicar medidas en JS.
      const card = el.querySelector<HTMLElement>(itemSelector);
      const step = card ? card.offsetWidth + gap : el.clientWidth * 0.8;

      el.scrollBy({
        left: step * direction,
        behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
          ? "auto"
          : "smooth",
      });
    },
    [itemSelector, gap],
  );

  return { ref, atStart, atEnd, scrollByCard };
}
